import React, {useEffect, useState} from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography
} from "@mui/material";
import './AttachmentsComponent.scss';
import {ContactData} from "./DocumentData";
import {useKeycloak} from "@react-keycloak/web";
import {ePismoClientInstance} from "../Service/EPismoClient";

export interface ContactGroup {
    id: number;
    name: string
}

interface ContactGroupDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (contacts: ContactData[]) => void;
}

export default function ContactGroupDialog({open, onClose, onConfirm}: ContactGroupDialogProps) {
    const [selectedContacts, setSelectedContacts] = useState<ContactData[]>([]);
    const [focusedGroup, setFocusedGroup] = useState<ContactGroup | null>(null);
    const [contactGroups, setContactGroups] = useState<ContactGroup[]>([]);
    const [positions, setPositions] = useState<ContactData[]>([])
    const [filterText, setFilterText] = useState("");
    const [groupMembers, setGroupMembers] = useState<Record<number, ContactData[]>>({});
    const keycloak = useKeycloak();

    const fetchGroupMembers = async (groupId: number) => {
        if (groupMembers[groupId]) return groupMembers[groupId];
        try {
            const resp = await ePismoClientInstance.get("/v1/addresses/groups/" + groupId, keycloak);
            const members: ContactData[] = resp.data;
            setGroupMembers(prev => ({ ...prev, [groupId]: members }));
            return members;
        } catch (error) {
            console.error(`Failed to fetch members for group ${groupId}`, error);
            return [];
        }
    };

    const handleToggle = (group: ContactGroup) => async () => {
        const members = await fetchGroupMembers(group.id);
        const memberIds = new Set(members.map(m => m.id));
        const isAllSelected = members.every(m => selectedContacts.some(sc => sc.id === m.id));

        if (isAllSelected) {
            // Remove all members of this group
            setSelectedContacts(prev => prev.filter(c => !memberIds.has(c.id)));
        } else {
            // Add all members of this group
            setSelectedContacts(prev => {
                const map = new Map(prev.map(c => [c.id, c]));
                members.forEach(m => map.set(m.id, m));
                return Array.from(map.values());
            });
        }
    };

    const handleContactToggle = (contact: ContactData) => () => {
        const currentIndex = selectedContacts.findIndex(c => c.id === contact.id);
        const newCheckedContacts = [...selectedContacts];

        if (currentIndex === -1) {
            newCheckedContacts.push(contact);
        } else {
            newCheckedContacts.splice(currentIndex, 1);
        }

        setSelectedContacts(newCheckedContacts);
    };

    const handleGroupClick = (group: ContactGroup) => {
        setFocusedGroup(group);
    }

    const handleConfirm = () => {
        onConfirm(selectedContacts);
        onClose();
    }

    useEffect(() => {
        ePismoClientInstance.get("/v1/addresses/groups", keycloak).then(resp => {
            const groups: ContactGroup[] = resp.data;
            setContactGroups(groups);
            // Pre-fetch all group members to show correct checkbox states immediately
            groups.forEach(group => fetchGroupMembers(group.id));
        });
    }, []);

    useEffect(() => {
        if(focusedGroup) {
            fetchGroupMembers(focusedGroup.id).then(members => setPositions(members));
        }
    }, [focusedGroup]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="contact-group-dialog"
        >
            <DialogTitle>Wysyłka masowa</DialogTitle>
            <DialogContent dividers className="dialog-content">
                <Grid container spacing={2} sx={{height: 400}}>
                    {/* Left: List of Groups */}
                    <Grid item xs={6} className="left-panel" sx={{overflowY: 'auto', maxHeight: '100%'}}>
                        <Typography variant="subtitle2" sx={{mb: 1}}>Grupy</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Filtruj grupy..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            sx={{ mb: 1 }}
                        />
                        <List dense disablePadding>
                            {contactGroups.filter(group => group.name.toLowerCase().includes(filterText.toLowerCase())).map((group) => {
                                const labelId = `group-list-item-${group.id}-label`;
                                const members = groupMembers[group.id] || [];
                                const selectedInGroup = members.filter(m => selectedContacts.some(sc => sc.id === m.id)).length;
                                const isAllSelected = members.length > 0 && selectedInGroup === members.length;
                                const isSomeSelected = members.length > 0 && selectedInGroup > 0 && selectedInGroup < members.length;

                                return (
                                    <ListItem
                                        key={group.id}
                                        disablePadding
                                    >
                                        <ListItemButton 
                                            selected={focusedGroup?.id === group.id}
                                            onClick={() => handleGroupClick(group)}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isAllSelected}
                                                    indeterminate={isSomeSelected}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggle(group)();
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText id={labelId} primary={group.name} />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Grid>
                    
                    {/* Right: List of Contacts in Group */}
                    <Grid item xs={6} className="right-panel" sx={{overflowY: 'auto', maxHeight: '100%'}}>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {focusedGroup ? `Kontakty: ${focusedGroup?.name}` : 'Wybierz grupę'}
                        </Typography>
                        <List dense>
                            {positions.map((contact) => {
                                const labelId = `contact-list-item-${contact.id}-label`;
                                return (
                                    <ListItem
                                        key={contact.id}
                                        disablePadding
                                    >
                                        <ListItemButton
                                            onClick={handleContactToggle(contact)}
                                        >
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedContacts.some(c => c.id === contact.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    inputProps={{ 'aria-labelledby': labelId }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                id={labelId}
                                                primary={contact.name}
                                                secondary={contact.address}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                );
                            })}
                            {positions.length === 0 && focusedGroup && (
                                <Typography variant="body2" color="text.secondary" sx={{p: 2}}>Brak kontaktów w grupie</Typography>
                            )}
                        </List>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions className="dialog-actions">
                <Button onClick={onClose} className="btn-cancel">Anuluj</Button>
                <Button onClick={handleConfirm} variant="contained">Zatwierdź grupy</Button>
            </DialogActions>
        </Dialog>
    );
}
